import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Empty, ErrorState, Field, Loading, Modal, Select, TextInput, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { dateTime } from '../../lib/format';

/**
 * The security incident register. Alerts open incidents on their own (source
 * is the rule's name); people open the rest. It records what happened and
 * what was done - whether anything must be reported, to whom and by when is
 * decided outside the software (docs/privacy/INCIDENT-RESPONSE.md).
 */

type Status = 'OPEN' | 'INVESTIGATING' | 'CONTAINED' | 'RESOLVED' | 'CLOSED';
type Severity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

interface Incident {
  id: string;
  reference: string;
  type: string;
  severity: Severity;
  status: Status;
  title: string;
  description: string;
  detectedAt: string;
  reportedAt: string;
  tenantId: string | null;
  propertyId: string | null;
  affectedSystems: string[];
  affectedDataCategories: string[];
  affectedSubjectsEstimate: number | null;
  containmentAction: string | null;
  resolution: string | null;
  source: string;
  closedAt: string | null;
}

const TYPES: Array<[string, string]> = [
  ['DATA_BREACH', 'Personal data breach'],
  ['UNAUTHORIZED_ACCESS', 'Unauthorised access'],
  ['CREDENTIAL_EXPOSURE', 'Credential exposure'],
  ['DEVICE_LOSS', 'Lost or stolen device'],
  ['BACKUP_EXPOSURE', 'Backup exposure'],
  ['SUSPICIOUS_EXPORT', 'Suspicious export'],
  ['OTHER', 'Other'],
];
const typeLabel = (type: string) => TYPES.find(([value]) => value === type)?.[1] ?? type;

const SEVERITY_CHIP: Record<Severity, string> = { LOW: '', MEDIUM: 'warn', HIGH: 'danger', CRITICAL: 'danger' };
const NEXT: Record<Status, Status[]> = {
  OPEN: ['INVESTIGATING', 'CONTAINED', 'RESOLVED', 'CLOSED'],
  INVESTIGATING: ['CONTAINED', 'RESOLVED', 'CLOSED'],
  CONTAINED: ['RESOLVED', 'CLOSED'],
  RESOLVED: ['CLOSED', 'INVESTIGATING'],
  CLOSED: [],
};

const list = (text: string) =>
  text
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

export function IncidentsTab() {
  const [openOnly, setOpenOnly] = useState(true);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState<string | null>(null);
  const incidents = useQuery({
    queryKey: ['platform-incidents', openOnly],
    queryFn: () => api.get<Incident[]>(`/api/platform/privacy/incidents?open=${openOnly}`),
  });
  if (incidents.isLoading) return <Loading />;
  if (incidents.error) return <ErrorState error={incidents.error} onRetry={() => void incidents.refetch()} />;
  const rows = incidents.data ?? [];
  return (
    <div className="card">
      <div className="card-head">
        <h3>Security incidents</h3>
        <div className="row gap-6">
          <label className="checkbox">
            <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />
            Open only
          </label>
          <button className="btn sm primary" onClick={() => setCreating(true)}>
            Report an incident
          </button>
        </div>
      </div>
      <div className="card-body small muted">
        Security alerts open incidents here on their own. Whether an incident must be reported - to the Data
        Protection Board, to hotels, to the people affected - and by when, is a legal decision: follow
        docs/privacy/INCIDENT-RESPONSE.md. No deadline is computed here.
      </div>
      {rows.length === 0 ? (
        <Empty message={openOnly ? 'No open incidents' : 'No incidents recorded'} />
      ) : (
        <div className="table-wrap">
          <table className="tbl tbl-stack">
            <thead>
              <tr>
                <th>Reference</th>
                <th>What</th>
                <th>Severity</th>
                <th>Status</th>
                <th>Detected</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="clickable" onClick={() => setViewing(row.id)}>
                  <td className="mono">{row.reference}</td>
                  <td>
                    <b>{row.title}</b>
                    <div className="xs muted">{typeLabel(row.type)}</div>
                  </td>
                  <td>
                    <span className={`chip ${SEVERITY_CHIP[row.severity]}`}>{row.severity}</span>
                  </td>
                  <td>{row.status}</td>
                  <td>{dateTime(row.detectedAt)}</td>
                  <td className="xs">{row.source === 'MANUAL' ? 'Reported' : row.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating ? <ReportIncident onClose={() => setCreating(false)} /> : null}
      {viewing ? <IncidentDetail id={viewing} onClose={() => setViewing(null)} /> : null}
    </div>
  );
}

function ReportIncident({ onClose }: { onClose: () => void }) {
  const { push, fail } = useToast();
  const queryClient = useQueryClient();
  const [type, setType] = useState('UNAUTHORIZED_ACCESS');
  const [severity, setSeverity] = useState<Severity>('MEDIUM');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [detectedAt, setDetectedAt] = useState(() => new Date().toISOString().slice(0, 16));
  const [systems, setSystems] = useState('');
  const [categories, setCategories] = useState('');
  const create = useMutation({
    mutationFn: () =>
      api.post('/api/platform/privacy/incidents', {
        type,
        severity,
        title,
        description,
        detectedAt: new Date(detectedAt).toISOString(),
        affectedSystems: list(systems),
        affectedDataCategories: list(categories),
      }),
    onSuccess: () => {
      push('Incident recorded', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['platform-incidents'] });
      onClose();
    },
    onError: fail,
  });
  return (
    <Modal
      title="Report an incident"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={create.isPending || !title.trim() || description.trim().length < 3}
            onClick={() => create.mutate()}
          >
            Record
          </button>
        </>
      }
    >
      <div className="col gap-14">
        <div className="form-grid">
          <Select label="Kind" value={type} onChange={(e) => setType(e.target.value)}>
            {TYPES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <Select label="Severity" value={severity} onChange={(e) => setSeverity(e.target.value as Severity)}>
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
            <option value="CRITICAL">Critical</option>
          </Select>
        </div>
        <TextInput label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <Field label="What happened" hint="Facts, not guesses. No personal data you do not need to record.">
          <textarea className="input" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <TextInput label="Detected" type="datetime-local" value={detectedAt} onChange={(e) => setDetectedAt(e.target.value)} />
        <TextInput label="Systems affected" hint="Comma separated" value={systems} onChange={(e) => setSystems(e.target.value)} />
        <TextInput label="Kinds of data affected" hint="Comma separated" value={categories} onChange={(e) => setCategories(e.target.value)} />
      </div>
    </Modal>
  );
}

function IncidentDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { session } = useAuth();
  const canEdit = session?.platformRole === 'SUPERADMIN';
  const { push, fail } = useToast();
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ['platform-incident', id],
    queryFn: () => api.get<Incident>(`/api/platform/privacy/incidents/${id}`),
  });
  const [status, setStatus] = useState<Status | ''>('');
  const [containment, setContainment] = useState<string | null>(null);
  const [resolution, setResolution] = useState<string | null>(null);
  const [estimate, setEstimate] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/api/platform/privacy/incidents/${id}`, {
        ...(status ? { status } : {}),
        ...(containment !== null ? { containmentAction: containment } : {}),
        ...(resolution !== null ? { resolution } : {}),
        ...(estimate !== null ? { affectedSubjectsEstimate: estimate === '' ? null : Number(estimate) } : {}),
      }),
    onSuccess: () => {
      push('Incident updated', 'ok');
      setStatus('');
      void queryClient.invalidateQueries({ queryKey: ['platform-incidents'] });
      void queryClient.invalidateQueries({ queryKey: ['platform-incident', id] });
    },
    onError: fail,
  });

  if (detail.isLoading || !detail.data) {
    return (
      <Modal title="Incident" onClose={onClose}>
        {detail.error ? <ErrorState error={detail.error} /> : <Loading />}
      </Modal>
    );
  }
  const row = detail.data;
  return (
    <Modal title={`${row.reference} - ${row.title}`} onClose={onClose} size="wide">
      <div className="col gap-14">
        <div className="small">
          {typeLabel(row.type)} · <span className={`chip ${SEVERITY_CHIP[row.severity]}`}>{row.severity}</span> ·{' '}
          {row.status} · detected {dateTime(row.detectedAt)} · recorded {dateTime(row.reportedAt)}
        </div>
        <div className="privacy-doc small">{row.description}</div>
        {row.affectedSystems.length ? <div className="small">Systems: {row.affectedSystems.join(', ')}</div> : null}
        {row.affectedDataCategories.length ? (
          <div className="small">Data: {row.affectedDataCategories.join(', ')}</div>
        ) : null}
        {canEdit && row.status !== 'CLOSED' ? (
          <>
            <Select label="Move to" value={status} onChange={(e) => setStatus(e.target.value as Status)}>
              <option value="">Keep as {row.status.toLowerCase()}</option>
              {NEXT[row.status].map((next) => (
                <option key={next} value={next}>
                  {next.toLowerCase()}
                </option>
              ))}
            </Select>
            <Field label="Containment - what was done to stop it">
              <textarea
                className="input"
                rows={3}
                value={containment ?? row.containmentAction ?? ''}
                onChange={(e) => setContainment(e.target.value)}
              />
            </Field>
            <Field label="Resolution" hint="Needed to close">
              <textarea
                className="input"
                rows={3}
                value={resolution ?? row.resolution ?? ''}
                onChange={(e) => setResolution(e.target.value)}
              />
            </Field>
            <TextInput
              label="People affected (estimate)"
              type="number"
              min={0}
              value={estimate ?? (row.affectedSubjectsEstimate === null ? '' : String(row.affectedSubjectsEstimate))}
              onChange={(e) => setEstimate(e.target.value)}
            />
            <div>
              <button className="btn primary" disabled={save.isPending} onClick={() => save.mutate()}>
                Save
              </button>
            </div>
          </>
        ) : (
          <>
            {row.containmentAction ? <div className="small">Containment: {row.containmentAction}</div> : null}
            {row.resolution ? <div className="small">Resolution: {row.resolution}</div> : null}
          </>
        )}
      </div>
    </Modal>
  );
}
